<script setup lang="ts">
import type {FileDiffPreview} from '../../types/previews.ts';
import htmx from 'htmx.org';

import {createApp, onMounted, ref, onBeforeUnmount, onUnmounted, inject, type Ref} from 'vue';
import {initAllSpice} from '../../../features/allspice.ts';
import {initImageDiff} from '../../../features/imagediff.ts';
import type {Store} from '../../store.ts';
import type {MultiPageDiff, ProjectGroupNode} from '../../types/file_tree.ts';
import {initViewedCheckboxListener} from './utils.ts';
import {initDiffHeaderPopup} from '../../../features/repo-diff.ts';
import MultiPageComponentSearch from '../MultiPageComponentSearch.vue';
import {SvgIcon} from '../../../svg.ts';
import {getPinia} from '../../stores/pinia.ts';
const store = inject<Store>('store');

const preview = defineModel<FileDiffPreview>({
  type: Object,
  required: true,
});

const htmlContainer: Ref<null | HTMLElement> = ref(null);
const cleanup: Array<() => void> = [];
const emit = defineEmits(['warnings']);

onMounted(() => {
  if (htmlContainer.value === null) {
    return;
  }

  htmlContainer.value.append(preview.value.html);

  if (preview.value.commentHtml) {
    // replace the comment section with comment section shared for all pages of multi-page files
    const conversationContainer = htmlContainer.value.querySelector('.code-conversations');
    if (conversationContainer) {
      // we have to clone here instead of using it directly because preview.value.commentHtml is actually the data source for our comments!
      // we also need to remove all the comments specific to a single page before replacing the comments
      const clonedCommentHtml: HTMLElement = preview.value.commentHtml.cloneNode(true) as HTMLElement;
      const pageSpecificComments = Array.from(clonedCommentHtml.querySelectorAll('.conversation-holder[data-allspice-subpath]'));
      for (const comment of pageSpecificComments) {
        if (comment.getAttribute('data-allspice-subpath').length > 0) {
          // this is a single page comment, so remove it
          comment.remove();
        }
      }

      conversationContainer.replaceWith(clonedCommentHtml);
    }
  }

  emit('warnings', preview.value.warnings);

  // since we are dynamically adding to the dom, we need to reprocess the htmx elements
  // note that we are processing the element in the DOM, not the preview object
  htmx.process(htmlContainer.value);

  const viewCheckboxCleanup = initViewedCheckboxListener(store);
  if (viewCheckboxCleanup) {
    cleanup.push(viewCheckboxCleanup);
  }

  htmlContainer.value.querySelector('.fold-file')?.remove();
  for (const e of htmlContainer.value.querySelectorAll('[data-folded]')) {
    // Remove the file fold button if it exists and ensure we are unfolded
    e.setAttribute('data-folded', 'false');
    // Set data-mounted so that elements can know they were remounted in the DOM
    e.setAttribute('data-mounted', 'true');
  }

  // set the vuejs property to unfolded
  const allspiceDiffElem = htmlContainer.value.querySelector('allspice-diff');
  if (allspiceDiffElem) {
    allspiceDiffElem.setAttribute(':folded', 'false');
    // Remove the size check to force it to always render
    allspiceDiffElem.setAttribute(':ignore-size-check', 'true');
  }

  // For multi-page-diff files in project view, replace the legacy DocumentSearch
  // with MultiPageComponentSearch so the parent header gets cross-page search.
  // Individual schematic documents that belong to a project get the same
  // component, scoped to their project-group, so net search spans the project
  // even though each document is diffed on its own.
  let searchFile: MultiPageDiff | ProjectGroupNode | null = null;
  let searchDocumentNameHash = '';
  if (store) {
    const diffBox = htmlContainer.value.querySelector('.diff-file-box');
    const nameHash = diffBox?.id?.replace('diff-', '');
    searchDocumentNameHash = nameHash ?? '';
    const node = nameHash ? store.hashToPathMap.value.get(nameHash) : null;
    if (node?.ty === 'multi-page-diff') {
      searchFile = node;
    } else if (node?.ty === 'sch-diff' && nameHash) {
      searchFile = store.projectGroupByChildHash.value.get(nameHash) ?? null;
    }
    if (searchFile) {
      const existingSearchWrapper = htmlContainer.value.querySelector('[data-multi-page-search]');
      const docSearchWrapper = existingSearchWrapper ? null : htmlContainer.value.querySelector('document-search')?.closest('.allspice-vue-mount');
      if (!existingSearchWrapper && docSearchWrapper) {
        // Remove allspice-vue-mount class so initAllSpice skips this element,
        // then replace its content with a mount target for our component.
        docSearchWrapper.classList.remove('allspice-vue-mount');
        docSearchWrapper.setAttribute('data-multi-page-search', '');
        docSearchWrapper.innerHTML = '<div></div>';
      } else if (!existingSearchWrapper) {
        // No search header on this document (e.g. a non-schematic file); nothing
        // to enhance.
        searchFile = null;
      }
    }
  }

  // note: this must be the last thing we do because we modify the prop
  initAllSpice();

  // Mount MultiPageComponentSearch into the prepared target after initAllSpice
  if (searchFile) {
    const target = htmlContainer.value.querySelector('[data-multi-page-search] > div') as HTMLElement | null;
    if (target) {
      const searchApp = createApp(MultiPageComponentSearch, {file: searchFile, documentNameHash: searchDocumentNameHash});
      searchApp.use(getPinia());
      searchApp.provide('store', store);
      searchApp.component('SvgIcon', SvgIcon);
      searchApp.mount(target);
      cleanup.push(() => searchApp.unmount());
    }
  }

  // Signal re-attached components (DiffFilter, etc.) to re-sync their
  // state with the store. In the project view, Vue apps inside the
  // preview HTML are reused across navigations (initAllSpice skips
  // already-mounted elements), so components like DiffFilter never get a
  // fresh inspectordataload event on return visits. This event lets them
  // re-apply stored overrides and sync hiddenDataIds.
  const diffBox = htmlContainer.value.querySelector('.diff-file-box');
  if (diffBox) {
    diffBox.dispatchEvent(new CustomEvent('diff-state-resync'));
  }

  // initPdfViewer is now handled by the global observer via data-global-init="initPdfViewer"

  initImageDiff();

  initDiffHeaderPopup();
});

onBeforeUnmount(() => {
  if (htmlContainer.value === null) {
    return;
  }

  // Set `data-mounted` to false so that we can reset it when we mount again.
  for (const e of htmlContainer.value.querySelectorAll('[data-folded]')) e.setAttribute('data-mounted', 'false');

  // Since the state for these elements in stored in the DOM, we need to save it back to the preview object
  preview.value.html = htmlContainer.value.firstElementChild;
});

onUnmounted(() => {
  // run all cleanup functions
  for (const fn of cleanup) {
    fn();
  }
});

</script>

<template>
  <div ref="htmlContainer" />
</template>
